import { Edit, Trash2, Eye, Radio, Play, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import TablePagination from "@/components/TablePagination";
import type {
  Match,
  MatchFilters,
  MatchStatus,
  Phase,
  Pagination,
  Gameweek,
} from "@/types/api.types";

interface MatchTableProps {
  matches: Match[];
  isLoading: boolean;
  filters: MatchFilters;
  onFiltersChange: (filters: MatchFilters) => void;
  onEdit: (match: Match) => void;
  onView: (match: Match) => void;
  onDelete: (match: Match) => void;
  onUpdateStatus: (match: Match, status: MatchStatus) => void;
  isDeleting: boolean;
  pagination?: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  gameweeks?: Gameweek[];
}

const STATUS_COLORS: Record<MatchStatus, string> = {
  SCHEDULED: "bg-gray-100 text-gray-700",
  LIVE: "bg-green-100 text-green-700 animate-pulse",
  COMPLETED: "bg-blue-100 text-blue-700",
  POSTPONED: "bg-yellow-100 text-yellow-700",
  CANCELLED: "bg-red-100 text-red-700",
};

const PHASES: Phase[] = [
  "GROUP_STAGE",
  "ROUND_OF_32",
  "ROUND_OF_16",
  "QUARTER_FINAL",
  "SEMI_FINAL",
  "THIRD_PLACE",
  "FINAL",
];

const STATUSES: MatchStatus[] = [
  "SCHEDULED",
  "LIVE",
  "COMPLETED",
  "POSTPONED",
  "CANCELLED",
];


const formatPhase = (phase: string) =>
  phase.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return {
    date: date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

// Which statuses can transition to which
const VALID_TRANSITIONS: Record<MatchStatus, MatchStatus[]> = {
  SCHEDULED: ["LIVE", "POSTPONED", "CANCELLED"],
  LIVE: ["COMPLETED"],
  COMPLETED: [],
  POSTPONED: ["SCHEDULED", "CANCELLED"],
  CANCELLED: [],
};

export default function MatchTable({
  matches,
  isLoading,
  filters,
  onFiltersChange,
  onEdit,
  onView,
  onDelete,
  onUpdateStatus,
  isDeleting,
  pagination,
  onPageChange,
  onPageSizeChange,
  gameweeks = [],
}: MatchTableProps) {
  const gameweekMap = new Map(gameweeks.map((gw) => [gw.id, gw]));
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters + Stats */}
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <Input
            placeholder="Search by nation or venue..."
            value={filters.search || ""}
            onChange={(e) =>
              onFiltersChange({ ...filters, search: e.target.value })
            }
            className="max-w-xs"
          />

          <Select
            value={filters.phase || "all"}
            onValueChange={(val) => {
              const nextPhase = val === "all" ? undefined : (val as Phase);
              // If the chosen phase no longer matches the active gameweek's
              // phase, drop the gameweek filter so the user doesn't end up
              // with an empty result set they have to clear manually.
              const activeGw = filters.gameweekId
                ? gameweekMap.get(filters.gameweekId)
                : undefined;
              const keepGw =
                !nextPhase ||
                !activeGw ||
                activeGw.phase === nextPhase;
              onFiltersChange({
                ...filters,
                phase: nextPhase,
                gameweekId: keepGw ? filters.gameweekId : undefined,
              });
            }}
          >
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Phase" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Phases</SelectItem>
              {PHASES.map((p) => (
                <SelectItem key={p} value={p}>
                  {formatPhase(p)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {(() => {
            // When a phase is picked, scope the matchweek list to that phase
            // so the admin only sees the 3 matchweeks inside (e.g. Group Stage).
            // When no phase is picked, show every gameweek.
            const scopedGameweeks = filters.phase
              ? gameweeks.filter((gw) => gw.phase === filters.phase)
              : gameweeks;
            if (scopedGameweeks.length === 0) return null;
            return (
              <Select
                value={
                  filters.gameweekId ? String(filters.gameweekId) : "all"
                }
                onValueChange={(val) =>
                  onFiltersChange({
                    ...filters,
                    gameweekId: val === "all" ? undefined : Number(val),
                  })
                }
              >
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Matchweek" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Matchweeks</SelectItem>
                  {scopedGameweeks.map((gw) => (
                    <SelectItem key={gw.id} value={String(gw.id)}>
                      {gw.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          })()}

          <Select
            value={filters.status || "all"}
            onValueChange={(val) =>
              onFiltersChange({
                ...filters,
                status: val === "all" ? undefined : (val as MatchStatus),
              })
            }
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-gray-100 text-gray-700 font-medium">{pagination?.total ?? matches.length} Total</Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">{matches.filter((m) => m.status === "LIVE").length} Live</Badge>
          <Badge className="bg-blue-100 text-blue-700 font-medium">{matches.filter((m) => m.status === "SCHEDULED").length} Scheduled</Badge>
          <Badge className="bg-purple-100 text-purple-700 font-medium">{matches.filter((m) => m.status === "COMPLETED").length} Completed</Badge>
        </div>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Match</TableHead>
            <TableHead>Score</TableHead>
            <TableHead>Phase / Stage</TableHead>
            <TableHead>Matchweek</TableHead>
            <TableHead>Kickoff</TableHead>
            <TableHead>Venue</TableHead>
            <TableHead>Events</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {matches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                No matches found
              </TableCell>
            </TableRow>
          ) : (
            matches.map((match) => {
              const { date, time } = formatDate(match.kickoffTime);
              const transitions = VALID_TRANSITIONS[match.status];

              return (
                <TableRow key={match.id}>
                  {/* Match Teams */}
                  <TableCell>
                    <div className="flex items-center gap-2 w-65">
                      <div className="flex items-center gap-1 min-w-0 flex-1">
                        {match.homeNation.flagUrl && (
                          <img
                            src={match.homeNation.flagUrl}
                            className="w-6 h-4 object-cover rounded shrink-0"
                            alt=""
                          />
                        )}
                        <span
                          className="font-medium text-sm truncate"
                          title={match.homeNation.name}
                        >
                          {match.homeNation.name}
                        </span>
                      </div>
                      <span className="text-gray-400 text-xs font-bold shrink-0">
                        vs
                      </span>
                      <div className="flex items-center gap-1 min-w-0 flex-1">
                        {match.awayNation.flagUrl && (
                          <img
                            src={match.awayNation.flagUrl}
                            className="w-6 h-4 object-cover rounded shrink-0"
                            alt=""
                          />
                        )}
                        <span
                          className="font-medium text-sm truncate"
                          title={match.awayNation.name}
                        >
                          {match.awayNation.name}
                        </span>
                      </div>
                    </div>
                  </TableCell>

                  {/* Score */}
                  <TableCell>
                    {match.status === "SCHEDULED" ? (
                      <span className="text-gray-400 text-sm">—</span>
                    ) : (
                      <span className="font-bold text-lg">
                        {match.homeScore} - {match.awayScore}
                      </span>
                    )}
                  </TableCell>

                  {/* Phase / Stage */}
                  <TableCell>
                    <div className="space-y-0.5">
                      <Badge variant="outline" className="text-xs">
                        {formatPhase(match.phase)}
                      </Badge>
                      {match.stage && (
                        <p className="text-xs text-gray-400">{match.stage}</p>
                      )}
                    </div>
                  </TableCell>

                  {/* Matchweek */}
                  <TableCell>
                    {match.gameweekId && gameweekMap.has(match.gameweekId) ? (
                      <div className="text-sm">
                        <span className="font-medium text-blue-700">
                          {gameweekMap.get(match.gameweekId)!.name}
                        </span>
                        <span className="ml-1 text-xs text-gray-400">
                          ({gameweekMap.get(match.gameweekId)!.status})
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </TableCell>

                  {/* Kickoff */}
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">{date}</div>
                      <div className="text-gray-400">{time}</div>
                    </div>
                  </TableCell>

                  {/* Venue */}
                  <TableCell>
                    <span className="text-sm text-gray-600">
                      {match.venue || "—"}
                    </span>
                  </TableCell>

                  {/* Events Count */}
                  <TableCell>
                    <span className="text-sm font-medium text-blue-600">
                      {match._count?.events || 0} events
                    </span>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {match.status === "LIVE" && (
                        <Radio className="h-3 w-3 text-green-600" />
                      )}
                      <Badge className={STATUS_COLORS[match.status]}>
                        {match.status}
                      </Badge>
                    </div>
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onView(match)}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>

                      {match.status === "SCHEDULED" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEdit(match)}
                          title="Edit match"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}

                      {/* Go Live — direct one-click for SCHEDULED matches */}
                      {match.status === "SCHEDULED" && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                          onClick={() => onUpdateStatus(match, "LIVE")}
                          title="Start match — set to LIVE"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Go Live
                        </Button>
                      )}

                      {/* Open Scorer — shortcut for LIVE matches */}
                      {match.status === "LIVE" && (
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2"
                          onClick={() => navigate("/live-scorer")}
                          title="Open Live Scorer"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Scorer
                        </Button>
                      )}

                      {/* Status transition dropdown — other transitions */}
                      {transitions.filter((s) => s !== "LIVE").length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs px-2"
                            >
                              More
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            {transitions.filter((s) => s !== "LIVE").map((status) => (
                              <DropdownMenuItem
                                key={status}
                                onClick={() => onUpdateStatus(match, status)}
                                className={
                                  status === "COMPLETED"
                                    ? "text-blue-600 font-medium"
                                    : status === "CANCELLED"
                                      ? "text-red-600"
                                      : ""
                                }
                              >
                                {status === "COMPLETED" && "✅ "}
                                {status === "POSTPONED" && "⏸️ "}
                                {status === "CANCELLED" && "❌ "}
                                Mark as {status}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDelete(match)}
                        disabled={isDeleting || match.status === "LIVE"}
                        title={
                          match.status === "LIVE"
                            ? "Cannot delete live match"
                            : "Delete"
                        }
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}
